import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { roomHierarchyMap } from "./utils/roomDeviceMap";
import { generateObservationURL } from "./utils/aesEncryptor2";
import "./VideoWall.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import logo2 from "./logo2.png";
import { useNavigate } from "react-router-dom";

const ResponsiveGridLayout = WidthProvider(Responsive);

const initialTileCount = 32;
const initialTiles = Array.from({ length: initialTileCount }, (_, i) => ({
  id: i,
  selectedFacility: "",
  selectedUnit: "",
  selectedRoom: "",
}));

const VideoTile = React.memo(({ tile, videoURL }) => {
  const [privacyActive, setPrivacyActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [talking, setTalking] = useState(false);

  const hasVideo = Boolean(videoURL);

  return (
    <div className="video-tile">
      <div className={`tile-header ${hasVideo ? "" : "tile-header--empty"}`}>
        {hasVideo ? (
          <>
            <span className="tile-room-name">{tile.selectedRoom}</span>
          </>
        ) : (
          <span className="tile-room-name">No room loaded</span>
        )}
      </div>

      {hasVideo ? (
        <div className="video-frame-wrapper">
          <iframe
            title={`Video ${tile.id}`}
            src={videoURL}
            allow="camera; microphone"
            frameBorder="0"
            className="video-frame"
          />

          {privacyActive && (
            <div className="privacy-overlay">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none"
                   stroke="currentColor" strokeWidth="1.5"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3h18v2H3z" />
                <path d="M5 5v15M19 5v15" />
                <path d="M9 5c0 4-3 6-3 9 0 2 1 4 3 5" />
                <path d="M15 5c0 4 3 6 3 9 0 2-1 4-3 5" />
                <path d="M3 20h18" />
              </svg>
              <span>Privacy Mode Active</span>
            </div>
          )}

          <div className="video-overlay-controls">
            {/* Privacy toggle */}
            <button
              type="button"
              className={`overlay-btn ${privacyActive ? "overlay-btn--active" : ""}`}
              title={privacyActive ? "Exit Privacy Mode" : "Privacy Mode / Curtain"}
              onClick={() => setPrivacyActive((p) => !p)}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                   stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3h18v2H3z" />
                <path d="M5 5v15M19 5v15" />
                <path d="M9 5c0 4-3 6-3 9 0 2 1 4 3 5" />
                <path d="M15 5c0 4 3 6 3 9 0 2-1 4-3 5" />
                <path d="M3 20h18" />
              </svg>
            </button>

            {/* Listen toggle */}
            <button
              type="button"
              className={`overlay-btn ${listening ? "overlay-btn--active" : ""}`}
              title={listening ? "Mute Audio from Room" : "Listen to Audio from Room"}
              onClick={() => setListening((l) => !l)}
            >
              {listening ? (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              )}
            </button>

            {/* Mic toggle */}
            <button
              type="button"
              className={`overlay-btn ${talking ? "overlay-btn--active" : ""}`}
              title={talking ? "Mic On (Talking)" : "Mic Off"}
              onClick={() => setTalking((t) => !t)}
            >
              {talking ? (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="2" width="6" height="12" rx="3" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                  <line x1="8" y1="22" x2="16" y2="22" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <line x1="2" y1="2" x2="22" y2="22" />
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
                  <path d="M15 9.34V5a3 3 0 0 0-5.94-.6" />
                  <path d="M17 16.95A7 7 0 0 1 5 12v-2" />
                  <path d="M19 10v2a7 7 0 0 1-.11 1.23" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                  <line x1="8" y1="22" x2="16" y2="22" />
                </svg>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="video-placeholder">No video loaded</div>
      )}
    </div>
  );
});

const VideoWall = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [tilesPerRow, setTilesPerRow] = useState(4);
  const [tiles, setTiles] = useState(initialTiles);
  const [videoURLs, setVideoURLs] = useState({});
  const [selectedFacility, setSelectedFacility] = useState("");
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleNavigateToReports = () => navigate("/outpatient-schedule");
  const handleNavigateToDashboard = () => navigate("/patient-dashboard");
  const handleNavigateToMonitor = () => navigate("/video-wall");
  const handleNavigateToCensus = () => navigate("/patient-list");

  const handleFacilityChangeGlobal = (e) => {
    setSelectedFacility(e.target.value);
    setSelectedUnits([]);
  };

  const toggleUnitSelection = (unit) => {
    setSelectedUnits((prev) =>
      prev.includes(unit) ? prev.filter((u) => u !== unit) : [...prev, unit]
    );
  };

  const handleLoadSelectedUnits = () => {
    if (!selectedFacility || selectedUnits.length === 0) return;

    const newRooms = selectedUnits.flatMap((unit) => {
      const roomMap = roomHierarchyMap[selectedFacility]?.[unit];
      if (!roomMap) return [];
      return Object.entries(roomMap).map(([roomName, deviceId]) => ({
        facility: selectedFacility,
        unit,
        room: roomName,
        deviceId,
      }));
    });

    const emptyTiles = tiles.filter((tile) => !tile.selectedRoom);
    const additionalNeeded = newRooms.length - emptyTiles.length;

    let updatedTiles = [...tiles];

    if (additionalNeeded > 0) {
      const newTileStartId = tiles.length;
      const newTileData = Array.from({ length: additionalNeeded }, (_, i) => ({
        id: newTileStartId + i,
        selectedFacility: "",
        selectedUnit: "",
        selectedRoom: "",
      }));
      updatedTiles = [...updatedTiles, ...newTileData];
    }

    const updated = [...updatedTiles];

    newRooms.forEach((roomData, index) => {
      const tile = updated[index];
      if (!tile) return;
      tile.selectedFacility = roomData.facility;
      tile.selectedUnit = roomData.unit;
      tile.selectedRoom = roomData.room;
    });

    const newURLs = { ...videoURLs };
    newRooms.forEach((roomData, index) => {
      newURLs[updated[index].id] = generateObservationURL(
        roomData.deviceId,
        "9tH0WsxD1Ko"
      );
    });

    setTiles(updated);
    setVideoURLs(newURLs);
  };

  const handleClearAll = () => {
    setTiles(initialTiles);
    setVideoURLs({});
  };

  // ✅ Scalable multi-IP version of handleAddPoseStream
  const handleAddPoseStream = () => {
    // List of IP cameras (RTSP or HTTP). Add/remove as needed.
    const ipCameras = [
      //"rtsp://192.168.86.50:554/stream1",
      //"rtsp://192.168.86.51:554/stream1",
      //"http://192.168.86.52:8080/video"
    ];

    // Build Flask URLs dynamically from IPs
    const baseFlaskServer = "http://192.168.86.42:5000";
    const rawPoseStreams = ipCameras.map(
      (ip) => `${baseFlaskServer}/video_feed/${encodeURIComponent(ip)}`
    );

    // You can also include local webcams if needed
    rawPoseStreams.unshift(`${baseFlaskServer}/video_feed/0`);

    setVideoURLs((prev) => {
      const updated = { ...prev };
      let availableTiles = tiles.filter((tile) => !prev[tile.id]);

      if (availableTiles.length < rawPoseStreams.length) {
        alert("Not enough empty tiles available for all pose streams.");
        return prev;
      }

      rawPoseStreams.forEach((url, index) => {
        const tile = availableTiles[index];
        if (tile) {
          updated[tile.id] = url;
        }
      });

      return updated;
    });
  };

  // ----- observe container width and compute tilesPerRow -----
  useEffect(() => {
    const minTileWidth = 480;
    const updateTilesPerRow = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const newTilesPerRow = Math.max(1, Math.floor(containerWidth / minTileWidth));
      setTilesPerRow((prev) => (prev === newTilesPerRow ? prev : newTilesPerRow));
    };

    updateTilesPerRow();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateTilesPerRow);
      if (containerRef.current) ro.observe(containerRef.current);
    } else {
      window.addEventListener("resize", updateTilesPerRow);
    }

    return () => {
      if (ro && containerRef.current) ro.unobserve(containerRef.current);
      else window.removeEventListener("resize", updateTilesPerRow);
    };
  }, []);

  const gridCols = Math.max(1, tilesPerRow * 2);

  const layout = useMemo(() => {
    return tiles.map((tile, index) => ({
      i: tile.id.toString(),
      x: (index % tilesPerRow) * 2,
      y: Math.floor(index / tilesPerRow),
      w: 2,
      h: 2,
    }));
  }, [tiles.length, tilesPerRow]);

  return (
    <div className={`video-wall ${isFullscreen ? "fullscreen" : ""}`}>
      <div className="wall-container">
        <img src={logo2} alt="Logo 2" className="wall-logo" />
        <div className="wall-header">
          <h1>Verge Monitor</h1>
        </div>

        {showControls && (
          <div className="unit-loader">
            <select value={selectedFacility} onChange={handleFacilityChangeGlobal}>
              <option value="">Select Facility</option>
              {Object.keys(roomHierarchyMap).map((facility) => (
                <option key={facility} value={facility}>
                  {facility}
                </option>
              ))}
            </select>

            {selectedFacility &&
              Object.keys(roomHierarchyMap[selectedFacility] || {}).map((unit) => (
                <button
                  key={unit}
                  className={`unit-button ${
                    selectedUnits.includes(unit) ? "selected" : ""
                  }`}
                  onClick={() => toggleUnitSelection(unit)}
                >
                  {unit}
                </button>
              ))}

            <button
              onClick={handleLoadSelectedUnits}
              disabled={selectedUnits.length === 0}
              className="load-button"
            >
              Load Selected Units
            </button>
            <button onClick={handleClearAll} className="navigate-button danger">
              Clear All
            </button>
            {/*<button onClick={handleAddPoseStream} className="load-button">
              CV Stream
            </button>
            <button onClick={handleNavigateToTaskList} className="navigate-button">
              Task List
            </button>*/}
            <button onClick={handleNavigateToCensus} className="top-nav-btn">Patient Census</button>
            <button onClick={handleNavigateToDashboard} className="top-nav-btn">Patient Dashboard</button>
            <button onClick={handleNavigateToReports} className="top-nav-btn">Outpatient Schedule</button>
          </div>
        )}
      </div>

      <div ref={containerRef} style={{ width: "100%" }}>
        <ResponsiveGridLayout
          className="layout"
          layouts={{ lg: layout }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
          cols={{ lg: gridCols, md: gridCols, sm: gridCols, xs: gridCols }}
          rowHeight={200}
          isResizable={true}
          isDraggable={true}
          margin={[10, 10]}
          draggableHandle=".tile-header" 
        >
          {tiles.map((tile, index) => (
            <div key={tile.id.toString()} data-grid={layout[index]}>
              <VideoTile
                tile={tile}
                videoURL={videoURLs[tile.id]}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>
    </div>
  );
};

export default VideoWall;
